import { useState } from 'react';
import { toast } from 'sonner';
import { cn } from '@/lib/cn';
import { Button, Eyebrow, Popover, PopoverContent, PopoverTrigger } from '@/components/ui';
import { useTaskStore } from '@/stores/taskStore';
import { useCalendarStore } from '@/stores/calendarStore';
import { useScheduleAction } from '@/features/capture/useCapture';
import { findFirstAvailableSlot, toBusyIntervals } from '@/lib/scheduling/findFirstSlot';
import { toDateKey } from '@/lib/dateKey';
import { formatScheduledFor } from '@/lib/time';
import type { Task } from '@/types';

export interface SchedulePopoverProps {
  task: Task;
  trigger: React.ReactNode;
}

function todayISO(): string {
  return toDateKey(new Date());
}

function tomorrowISO(): string {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return toDateKey(d);
}

function dateChipClass(active: boolean): string {
  return cn(
    'rounded-md border px-2 py-1 text-caption transition-colors pointer-coarse:min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset',
    active ? 'border-t1 bg-t1 text-canvas' : 'border-line-2 bg-bg text-t2 can-hover:hover:bg-card',
  );
}

const dateTimeInputClass =
  'rounded-md border border-line-2 bg-bg px-2 py-1 text-caption text-t1 font-mono tabular-nums pointer-coarse:min-h-[44px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-inset';

export function SchedulePopover({ task, trigger }: SchedulePopoverProps) {
  const [open, setOpen] = useState(false);
  const [date, setDate] = useState(todayISO);
  const [time, setTime] = useState('');
  const updateTask = useTaskStore((s) => s.updateTask);
  const { scheduleExistingTask } = useScheduleAction();

  const onAuto = () => {
    const slot = scheduleExistingTask(task);
    if (slot === null) toast.error(`No open slot for ${task.title} — pick a time?`);
    else toast.success(`Scheduled: ${task.title}`);
    setOpen(false);
  };

  const onSchedule = () => {
    const baseDate = new Date(`${date}T00:00:00`);
    // The native date field can be cleared to '' → `new Date('T00:00:00')` is an
    // Invalid Date. Bail so we never write a garbage date (or a "NaN" toast).
    if (Number.isNaN(baseDate.getTime())) return;
    let start: Date;
    if (time) {
      const [h, m] = time.split(':').map(Number);
      if (!Number.isFinite(h) || !Number.isFinite(m)) return;
      start = new Date(baseDate);
      start.setHours(h, m, 0, 0);
    } else {
      const tasks = useTaskStore.getState().tasks;
      const events = useCalendarStore.getState().events;
      const busy = toBusyIntervals(tasks, events, baseDate);
      const slot = findFirstAvailableSlot(baseDate, task.durationMinutes, busy);
      if (slot === null) {
        toast.error('No open slot that day — pick a time?');
        return;
      }
      start = slot;
    }
    const end = new Date(start.getTime() + task.durationMinutes * 60_000);
    updateTask(task.id, { status: 'scheduled', scheduledStart: start, scheduledEnd: end });
    toast.success(`Scheduled ${formatScheduledFor(start)}: ${task.title}`);
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent side="bottom" align="end" className="w-72 p-3">
        <div className="space-y-3">
          <div>
            <Eyebrow className="mb-1">Date</Eyebrow>
            <div className="flex gap-1">
              <button
                type="button"
                aria-pressed={date === todayISO()}
                onClick={() => setDate(todayISO())}
                className={dateChipClass(date === todayISO())}
              >
                Today
              </button>
              <button
                type="button"
                aria-pressed={date === tomorrowISO()}
                onClick={() => setDate(tomorrowISO())}
                className={dateChipClass(date === tomorrowISO())}
              >
                Tomorrow
              </button>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                aria-label="Date"
                // min-w-0 lets the native date input shrink next to the two
                // chips instead of pushing the row past the popover edge.
                className={cn('min-w-0 flex-1', dateTimeInputClass)}
              />
            </div>
          </div>
          <div>
            <Eyebrow className="mb-1">
              Time <span>(leave empty for auto-pick)</span>
            </Eyebrow>
            <input
              type="time"
              value={time}
              onChange={(e) => setTime(e.target.value)}
              aria-label="Time"
              className={cn('w-full', dateTimeInputClass)}
            />
          </div>
          <div className="flex gap-2 pt-1">
            <Button variant="secondary" size="sm" onClick={onAuto} className="flex-1">
              Auto-pick slot
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={onSchedule}
              disabled={!date}
              className="flex-1"
            >
              Schedule
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
